import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import apiService from '../../services/api';
import { isManagerOrAbove } from '../../lib/roles';
import { subscribeToDeliveryPersonsUpdates } from '../../services/socket';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { getTokens } from '../../utils/tokens';
import { Plus, Pencil, X, Download, Loader2 } from 'lucide-react';
import { exportDeliveryPersonsToExcel } from '../../utils/excelExport';
import {
  PageHeader, StatStrip, PrimaryBtn, GhostBtn, Toolbar,
  DataTable, Pagination, AvatarInitials,
} from '../ui/AdminPagePrimitives';
import { StatStripSkeleton, TableSkeleton } from '../ui/Skeleton';

const DeliveryPersons = () => {
  const { user } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.theme);
  const { t, isRTL } = useLanguage();
  const showToast = useToast();
  const tk = getTokens(mode);
  const { language } = useLanguage();

  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingPerson, setEditingPerson] = useState(null);
  const [deletingPerson, setDeletingPerson] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [exporting, setExporting] = useState(false);

  const canManage = isManagerOrAbove(user?.role);

  // ── style helpers ──────────────────────────────────────────────────────────
  const wrapperStyle = {
    background: tk.bg, color: tk.text, padding: 20, minHeight: '100%',
    fontFamily: language === 'ar' ? '"Cairo",sans-serif' : '"DM Sans",sans-serif',
    overflowY: 'auto',
  };
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
  };
  const modalBox = {
    background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14,
    padding: 24, maxWidth: 500, width: '100%', margin: '0 16px',
    maxHeight: '90vh', overflowY: 'auto',
  };
  const inputStyle = {
    width: '100%', height: 40, background: tk.sunken, border: `1px solid ${tk.border}`,
    borderRadius: 8, padding: '0 12px', color: tk.text, fontFamily: 'inherit',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase',
    letterSpacing: 0.4, marginBottom: 6, display: 'block',
  };

  // ── filtered list ──────────────────────────────────────────────────────────
  const filteredDeliveryPersons = deliveryPersons.filter((dp) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (dp.name || '').toLowerCase().includes(q) || (dp.phone || '').toLowerCase().includes(q);
  });

  // ── data ───────────────────────────────────────────────────────────────────
  useEffect(() => { loadDeliveryPersons(); }, []);

  useEffect(() => {
    const unsubscribe = subscribeToDeliveryPersonsUpdates(loadDeliveryPersons);
    return unsubscribe;
  }, []);

  const loadDeliveryPersons = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getDeliveryPersons();
      setDeliveryPersons(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err.message || t('alerts.failedToLoadDeliveryPersons'));
      setDeliveryPersons([]);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(filteredDeliveryPersons.length / itemsPerPage);
  const paginatedPersons = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredDeliveryPersons.slice(start, start + itemsPerPage);
  }, [filteredDeliveryPersons, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const handleEdit = (row) => setEditingPerson(row);

  // ── form modal ─────────────────────────────────────────────────────────────
  const FormModal = ({ person, onClose, onSave }) => {
    const [formData, setFormData] = useState(
      person ? { name: person.name || '', phone: person.phone || '' } : { name: '', phone: '' }
    );
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSaving(true);
      try {
        if (!formData.name?.trim()) throw new Error(t('settings.deliveryPersonNameRequired'));
        if (!formData.phone?.trim()) throw new Error(t('settings.deliveryPersonPhoneRequired'));
        if (formData.name.trim().length > 30) throw new Error(t('alerts.deliveryPersonNameMaxLength'));
        if (formData.phone.trim().length > 15) throw new Error(t('alerts.deliveryPersonPhoneMaxLength'));
        const phoneTrimmed = formData.phone.trim();
        const duplicate = deliveryPersons.find((dp) => dp.phone === phoneTrimmed && dp.id !== person?.id);
        if (duplicate) throw new Error(t('settings.deliveryPersonPhoneUnique'));
        const payload = { name: formData.name.trim(), phone: formData.phone.trim() };
        if (person) {
          await apiService.updateDeliveryPerson(person.id, payload);
        } else {
          await apiService.createDeliveryPerson(payload);
        }
        onSave();
      } catch (err) {
        const apiError = err.body?.error || err.message;
        const showError = apiError && apiError.includes('already exists')
          ? t('settings.deliveryPersonPhoneUnique')
          : (apiError || t('alerts.failedToSave').replace('{error}', t('alerts.unknownError')));
        showToast(showError, "error");
      }
      setSaving(false);
    };

    return (
      <div style={overlay}>
        <div style={modalBox}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
              {person ? t('deliveryPersons.editPerson') : t('deliveryPersons.addPerson')}
            </div>
            <button type="button" onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <X size={14} />
            </button>
          </div>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>{t('settings.deliveryPersonName')}</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} maxLength={30} style={inputStyle} required />
            </div>
            <div>
              <label style={labelStyle}>{t('settings.deliveryPersonPhone')}</label>
              <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })} maxLength={15} style={inputStyle} placeholder={t('settings.deliveryPersonPhone')} required />
            </div>
            <div style={{ display: 'flex', gap: 8, paddingTop: 6 }}>
              <button type="button" onClick={onClose} disabled={saving} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5 }}>
                {t('common.cancel')}
              </button>
              <button type="submit" disabled={saving} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.accent, border: 'none', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
                {saving ? t('common.loading') : person ? t('common.update') : t('deliveryPersons.addPerson')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ── delete confirm modal ───────────────────────────────────────────────────
  const DeleteConfirmModal = ({ person, onClose, onConfirm }) => (
    <div style={overlay}>
      <div style={modalBox}>
        <div style={{ fontSize: 14, fontWeight: 600, color: tk.text, marginBottom: 10 }}>
          {t('deliveryPersons.deletePerson')}
        </div>
        <div style={{ fontSize: 12.5, color: tk.textSecondary, marginBottom: 20 }}>
          {t('settings.deleteDeliveryPersonConfirm')} {person?.name ? `"${person.name}"` : ''}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5 }}>
            {t('common.cancel')}
          </button>
          <button onClick={() => { onConfirm(person.id); onClose(); }} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.danger, border: 'none', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600 }}>
            {t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div style={wrapperStyle} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        tk={tk}
        title={t('deliveryPersons.title') || t('nav.deliveryPersonnel')}
        subtitle={t('deliveryPersons.subtitle') || 'Delivery drivers'}
        actions={
          <>
            <GhostBtn tk={tk} icon={exporting ? Loader2 : Download} onClick={async () => {
              if (!deliveryPersons.length) return;
              setExporting(true);
              try {
                const labels = t('deliveryPersons.excelExport');
                const L = typeof labels === 'object' && labels !== null ? labels : {};
                await exportDeliveryPersonsToExcel(deliveryPersons, 'delivery-persons.xlsx', L);
              } catch (err) {
                console.error('Export failed', err);
              } finally {
                setExporting(false);
              }
            }}>
              {exporting ? t('monthlyClosing.downloading') : t('deliveryPersons.downloadExcel')}
            </GhostBtn>
            {canManage && (
              <PrimaryBtn tk={tk} icon={Plus} onClick={() => setShowAddModal(true)}>
                {t('deliveryPersons.addPerson') || 'Add driver'}
              </PrimaryBtn>
            )}
          </>
        }
      />

      {error && (
        <div style={{ background: tk.dangerSoft, color: tk.dangerText, border: `1px solid ${tk.danger}33`, borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <StatStripSkeleton tk={tk} count={1} />
      ) : (
        <StatStrip tk={tk} items={[
          { label: language === 'ar' ? 'إجمالي المندوبين' : 'Total drivers', value: String(deliveryPersons.length) },
        ]} />
      )}

      <Toolbar
        tk={tk}
        searchPlaceholder={t('deliveryPersons.searchPlaceholder') || 'Search drivers...'}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {loading ? (
        <TableSkeleton tk={tk} colWidths={[2.5, 1.5, 0.7]} />
      ) : (
        <DataTable
          tk={tk}
          emptyMessage={searchQuery ? (t('deliveryPersons.noResults') || 'No drivers found') : (t('settings.noDeliveryPersons') || 'No drivers found')}
          columns={[
            { key: 'name', label: t('settings.deliveryPersonName') || 'Name' },
            { key: 'phone', label: t('settings.deliveryPersonPhone') || 'Phone' },
            { key: 'actions', label: '' },
          ]}
          rows={paginatedPersons}
          renderCell={(row, key) => {
            if (key === 'name') return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <AvatarInitials name={row.name || '?'} tk={tk} size={28} />
                <span style={{ fontWeight: 500, color: tk.text }}>{row.name}</span>
              </div>
            );
            if (key === 'phone') return (
              <span style={{ fontFamily: '"JetBrains Mono",monospace', fontSize: 11.5, color: tk.textSecondary, direction: 'ltr' }}>{row.phone || '—'}</span>
            );
            if (key === 'actions') return (
              <div style={{ display: 'flex', gap: 4 }}>
                {canManage && <>
                  <button onClick={() => handleEdit(row)} style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.textSecondary }}>
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => setDeletingPerson(row)} style={{ width: 28, height: 28, background: tk.dangerSoft, border: `1px solid ${tk.danger}33`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.dangerText }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                  </button>
                </>}
              </div>
            );
            return <span style={{ color: tk.textSecondary, fontSize: 12 }}>{row[key] || '—'}</span>;
          }}
        />
      )}

      <Pagination tk={tk} currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {showAddModal && (
        <FormModal
          onClose={() => setShowAddModal(false)}
          onSave={() => { setShowAddModal(false); loadDeliveryPersons(); }}
        />
      )}
      {editingPerson && (
        <FormModal
          person={editingPerson}
          onClose={() => setEditingPerson(null)}
          onSave={() => { setEditingPerson(null); loadDeliveryPersons(); }}
        />
      )}
      {deletingPerson && (
        <DeleteConfirmModal
          person={deletingPerson}
          onClose={() => setDeletingPerson(null)}
          onConfirm={async (id) => {
            try {
              await apiService.deleteDeliveryPerson(id);
              loadDeliveryPersons();
            } catch (e) {
              showToast(e.body?.error || e.message || t('alerts.deleteFailed'), "warning");
            }
          }}
        />
      )}
    </div>
  );
};

export default DeliveryPersons;
